//@ts-check

import React, { useState } from "react"
import { useTranslation } from "react-i18next"

// Material
import { withStyles, makeStyles } from "@material-ui/core/styles"
import Typography from "@material-ui/core/Typography"
import ExpansionPanel from "@material-ui/core/ExpansionPanel"
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary"
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import List from "@material-ui/core/List"
import ListItem from "@material-ui/core/ListItem"
import ListItemAvatar from "@material-ui/core/ListItemAvatar"
import ListItemText from "@material-ui/core/ListItemText"
import Avatar from "@material-ui/core/Avatar"
import InfoIcon from "@material-ui/icons/Info"
import Popover from "@material-ui/core/Popover"
import { track } from "../utils/tracking"

import globalStyles from "./styles"

// Components
import Chip from "@material-ui/core/Chip"
import eyuiTheme from "../theme"
// Internal
import Button from "./button"
import SnippetSection from "./snippet-section"
import { NewWindow } from "./icons-svg"
import { getOutputItemStatus } from "shared/utils.js"

const CONSTS = require("shared/consts")
const { UI_RESULT_TYPES } = CONSTS

const StyledExpansionPanel = withStyles({
  root: {
    width: "100%",
    background: "transparent",
    boxShadow: "none",
    borderBottom: `1px solid ${eyuiTheme.palette.primary.lighter}`,
    paddingTop: 0,
    paddingLeft: 0,
    paddingBottom: 4,
    "&.Mui-expanded": {
      margin: 0,
      paddingTop: 0,
    },
  },
})(ExpansionPanel)

const StyledExpansionPanelSummary = withStyles({
  root: {
    display: "inline-flex",
    width: "100%",
    minHeight: "unset !important",
    paddingLeft: 0,
    cursor: "initial !important",
  },
  content: {
    overflow: "hidden",
    marginTop: "4px !important",
    marginBottom: "0 !important",
    cursor: "initial ",
  },
})(ExpansionPanelSummary)

const expandToggleWidth = 24 // the `width` of `.MuiListItemAvatar-root` (MUI) - see `expandToggle` class styling
const expandToggleMarginRight = 16 // the `margin-right` of `.MuiListItemAvatar-root` (MUI) - see `expandToggle` class styling

const StyledExpansionPanelDetails = withStyles({
  root: {
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: expandToggleWidth + expandToggleMarginRight, // to match `width` plus `margin-right` of `.MuiListItemAvatar-root` (MUI) - see `expandToggle` class styling
  },
})(ExpansionPanelDetails)

const ResultContent = withStyles(theme => ({
  root: {
    width: "100%",
    paddingBottom: theme.spacing(2),
  },
}))(Typography)

const generatePrimaryResultText = outputItem => {
  let primaryText = outputItem.title
  const { t } = useTranslation()
  if (String(outputItem.result).toLowerCase() == "n/a") {
    primaryText = `'${primaryText}': ${t("not_available_for_this_smart_contract")}`
  } else if (
    typeof outputItem.result === "string" &&
    outputItem.result.trim() !== "" &&
    outputItem.result.toLowerCase() !== "yes" &&
    outputItem.result.toLowerCase() !== "no" &&
    outputItem.result.toLowerCase() !== "success" &&
    outputItem.result.toLowerCase() !== "failure" &&
    outputItem.result.toLowerCase() !== "failed"
  ) {
    primaryText = `${primaryText}: ${outputItem.result}`
  }
  return primaryText
}

const secondaryStyling = {
  textOverflow: "ellipsis",
  overflow: "hidden",
  verticalAlign: "middle",
  marginTop: 4,
  cursor: "initial !important",
}

const StyledListItemText = withStyles({
  secondary: { ...secondaryStyling },
})(ListItemText)


const generateStatusAvatar = function({ outputItem }) {
  const classes = globalStyles()
  const { t } = useTranslation()
  var type, label, icon
  if (outputItem.noChips) {
    return <> </>
  }
  const testStatus = getOutputItemStatus(outputItem)
  var color,backgroundColor
  switch (testStatus) {
    case UI_RESULT_TYPES.PASS: {
      type = "success"
      label = t("passed")
      backgroundColor="#168736"
      color= "white"
      break
    }
    case UI_RESULT_TYPES.INFO: {
      type = ""
      label = t("informational")
      icon = <InfoIcon></InfoIcon>
      color=""
      backgroundColor= ""

      break
    }
    case UI_RESULT_TYPES.NOT_APPLICABLE: {
      type = ""
      label = t("n_a")
      break
    }
    default: {
      type = "error"
      label = t("failed")
      color = "white"
      backgroundColor= "#B9251C"
    }
  }

  return (
    <>
    <div css={`
    .MuiChip-root {
      background-color:${backgroundColor};
      color:${color};
    }
    `}>
      <Chip 
      type={type} icon={icon} label={label} />
    </div>
      &nbsp;
      {window["config"]?.SHOW_SWC &&
        outputItem.swc?.map(w => (
          <Chip
            type=""
            icon={<InfoIcon />}
            className={classes.swcChip}
            label={
              <a
                target="_blank"
                style={{ color: "inherit", textDecoration: "none" }}
                href={"https://swcregistry.io/docs/SWC-" + w}
              >
                SWC-{w}
              </a>
            }
          />
        ))}
    </>
  )
}

const listContainerStyles = {
  "&:hover": {
    filter: "brightness(95%)",
  },
  cursor: "pointer",
}

/**
 * @param props {{
 * outputItem:import('../../../backend/interfaces').TestResultWithTestData,
 * toggleExpanded,
 * setIsFailOpen,
 * setIsPassOpen
 * isPassOpen,
 * isFailOpen,
 * swc,
 * scrollToSource,
 * isSubCategoriesShown?
 * }}
 */
const OutputOneTabItem = function({
  outputItem,
  toggleExpanded,
  setIsPassOpen,
  setIsFailOpen,
  scrollToSource,
  isSubCategoriesShown,
}) {
  const { t } = useTranslation()
  const [duplicatesDropdownAnchor, setDuplicatesDropdownAnchor] = useState(null)
  const handleDuplicatesDropdownOpen = event => {
    setDuplicatesDropdownAnchor(event.currentTarget)
  }

  const handleDuplicatesDropdownClose = () => {
    setDuplicatesDropdownAnchor(null)
  }

  const isDropdownOpen = Boolean(duplicatesDropdownAnchor)
  const duplicatesDropdownButtonId = isDropdownOpen
    ? "duplicates-dropdown-button"
    : undefined

  const expandedIconRotation = outputItem.expanded ? 180 : 0
  const { swc, isPassOpen, isFailOpen } = outputItem

  const styles = makeStyles(theme => ({
    expandedIcon: {
      transform: `rotate(${expandedIconRotation}deg)`,
      transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      color: "rgba(0, 0, 0, 0.54)",
      marginTop: "0.5rem",
    },
    statusChipWrapper: {
      marginRight: 8,
    },
    secondaryText: {
      marginLeft: 8,
    },
    outputDescriptionParagraph: {
      marginTop: theme.spacing(2),
      wordBreak: "break-word",
    },
    detailsSectionHeader: {
      fontWeight: "bold",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    link: {
      fontSize: "0.95rem",
    },
    expandToggle: {
      minWidth: 0,
      marginRight: theme.spacing(2),
      cursor: "pointer",
    },
    itemHeading: {
      fontWeight: 600,
      cursor: "pointer",
    },
    subCategory: {
      fontSize: "0.8rem",
    },
  }))
  const classes = styles({})

  const showSecondaryText = !outputItem.expanded

  const generateSecondary = ({ outputItem, showSecondaryText }) => (
    <>
      {generateStatusAvatar({
        outputItem
      })}
      {showSecondaryText && (
        <span style={secondaryStyling} className={classes.secondaryText}>
          {(outputItem.description || "").split("\n")[0]}
        </span>
      )}
    </>
  )

  const splitWarning = warningString => {
    return warningString.split(CONSTS.WARNING_INDICATOR_STRING)
  }

  const formatDescription = (text, duplicates) => {
    const warningRegex = new RegExp(CONSTS.WARNING_INDICATOR_STRING)
    if (!duplicates || duplicates.length === 0 || !warningRegex.test(text)) {
      return text
    } else {
      const [lineDetails, warningMessage] = splitWarning(text)
      return (
        <>
          {lineDetails}{" "}
          <Chip
            label={`+${duplicates.length}`}
            onClick={handleDuplicatesDropdownOpen}
          />
          <Popover
            id={duplicatesDropdownButtonId}
            open={isDropdownOpen}
            anchorEl={duplicatesDropdownAnchor}
            onClose={handleDuplicatesDropdownClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <List>
              {duplicates.map(duplicate => {
                const [duplicateLineDetails] = splitWarning(duplicate)
                return (
                  <ListItem>
                    <ListItemText>{duplicateLineDetails}</ListItemText>
                  </ListItem>
                )
              })}
            </List>
          </Popover>{" "}
          {warningMessage}
        </>
      )
    }
  }

  const generateResultsItem = (line, duplicates) => {
    if (!line || typeof line !== "string") {
      return null
    }
    const text = line.trim()
    const firstWord = text.split(" ")[0]
    const recommendationText = text.slice(firstWord.length)

    if (firstWord.toLowerCase() === "recommendation:") {
      return (
        <>
          <div className={classes.detailsSectionHeader}>
            {t("recommendation_heading")}
          </div>
          <Typography>{recommendationText}</Typography>
        </>
      )
    } else {
      return (
        <Typography className={classes.outputDescriptionParagraph}>
          {formatDescription(text, duplicates)}
        </Typography>
      )
    }
  }

  const testStatus = getOutputItemStatus(outputItem)
  return (
    <StyledExpansionPanel expanded={outputItem.expanded}>
      <StyledExpansionPanelSummary aria-controls="panel1a-content">
        <>
          <ListItemAvatar
            onClick={function() {
              if (!outputItem.expanded) {
                track(CONSTS.MIXPANEL_EVENTS.REVIEW_TEST, {
                  title: outputItem.title,
                  testId: outputItem.code,
                  testResult: testStatus,
                })
              }
              toggleExpanded.apply(null, arguments)
            }}
            className={classes.expandToggle}
          >
            <ExpandMoreIcon className={classes.expandedIcon} />
          </ListItemAvatar>
          <StyledListItemText
            primary={
              <div
                onClick={function() {
                  if (!outputItem.expanded) {
                    track(CONSTS.MIXPANEL_EVENTS.REVIEW_TEST, {
                      title: outputItem.title,
                      testId: outputItem.code,
                      testResult: testStatus,
                    })
                  }
                  toggleExpanded.apply(null, arguments)
                }}
              >
                <span className={classes.itemHeading}>
                  {generatePrimaryResultText(outputItem)}
                </span>
                {isSubCategoriesShown && outputItem.subCategory && (
                  <span className={classes.subCategory}>
                    {" "}
                    | {outputItem.subCategory}
                  </span>
                )}                
              </div>
            }
            secondary={generateSecondary({
              outputItem,
              showSecondaryText,
            })}
          />
        </>
      </StyledExpansionPanelSummary>
      <StyledExpansionPanelDetails>
        <ResultContent>
          {(outputItem.description || "")
            .split("\n")
            .map(line => generateResultsItem(line, outputItem.duplicates))}
          {outputItem.highlights && (
            <SnippetSection
              snippets={outputItem.highlights}
              onViewSourceClick={scrollToSource}
            />
          )}
          {outputItem.recommendation && (
            <div>
              <div className={classes.detailsSectionHeader}>
                  {t("recommendation_heading")}
                </div>
                {outputItem.recommendation.split("\n").filter(item => item.trim() !== "").map(item => (
                  <Typography>
                    {item}
                    <br />
                  </Typography>
                ))}
            </div>
          )}
          {outputItem.links && (
            <div>
              <div className={classes.detailsSectionHeader}>
                {t("links_heading")}
              </div>
              {outputItem.links.map(url => (
                <Typography>
                  <a className={classes.link} href={url} target="_blank">{url}</a>
                  <br />
                </Typography>
              ))}
            </div>
          )}
          {outputItem.pass_input && !outputItem.pass_input_expected_results && (
            // Do not show example input for tests like COUNT OF STATEMENTS
            <LinkToMoreInfo
              title={t("show_example_pass_test")}
              isOpen={isPassOpen}
              setIsOpened={isOpen => setIsPassOpen(isOpen)}
            >
              <SolidityHighlight code={outputItem.pass_input} />
            </LinkToMoreInfo>
          )}
          {outputItem.fail_input && !outputItem.fail_input_expected_results && (
            // Do not show example input for tests like COUNT OF STATEMENTS
            <LinkToMoreInfo
              title={t("show_example_fail_test")}
              isOpen={isFailOpen}
              setIsOpened={isOpen => setIsFailOpen(isOpen)}
            >
              <SolidityHighlight code={outputItem.fail_input} />
            </LinkToMoreInfo>
          )}
        </ResultContent>
      </StyledExpansionPanelDetails>
    </StyledExpansionPanel>
  )
}

export default React.memo(OutputOneTabItem, (prev, newProps) => {
  return prev.outputItem.expanded == newProps.expanded
})

function LinkToMoreInfo({ title, children, isOpen, setIsOpened }) {
  const expandedIconRotation = isOpen ? 180 : 0

  const styles = makeStyles(theme => ({
    sectionWrapper: {
      marginTop: theme.spacing(2),
    },
    expandedIcon: {
      transform: `rotate(${expandedIconRotation}deg)`,
      transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      color: "rgba(0, 0, 0, 0.54)",
      marginRight: "0.5rem",
    },
    exampleSection: {
      paddingBottom: theme.spacing(3),
    },
    expandButton: {
      paddingLeft: 0,
      textAlign: "left",
    },
  }))
  const classes = styles({})

  return (
    <div className={classes.sectionWrapper}>
      <div
        onClick={() => {
          track("user click " + title)
          setIsOpened(!isOpen)
        }}
      >
        <Button variant="text" className={classes.expandButton}>
          <ExpandMoreIcon className={classes.expandedIcon} />
          {title}
        </Button>
      </div>
      {isOpen && <div className={classes.exampleSection}>{children}</div>}
    </div>
  )
}

function SolidityHighlight({ code }) {
  return (
    <React.Suspense fallback={null}>
      <code>
        {code}
      </code>
    </React.Suspense>
  )
}
